import Logo from '@/assets/Pitchster-logo-final.png';

export interface SkillReportExportOptions {
  teamSkills: {
    discovery: number;
    objections: number;
    product: number;
    closing: number;
  };
  reps: Array<{
    name: string;
    discovery: number;
    objections: number;
    product: number;
    closing: number;
    overall: number;
    trend: number;
  }>;
  dateFilter: string;
  logoUrl?: string;
}

function getFilterLabel(filter: string): string {
  switch (filter) {
    case '7': return 'Last 7 Days';
    case '15': return 'Last 15 Days';
    case '30': return 'Last 30 Days (1 Month)';
    case '60': return 'Last 2 Months';
    case '90': return 'Last 3 Months';
    case 'all': return 'All Time';
    default: return filter === 'all' ? 'All Time' : `Last ${filter} Days`;
  }
}

function getScoreColor(score: number): string {
  if (score >= 90) return '#16a34a'; // Green
  if (score >= 70) return '#5a9d50'; // Light Green
  if (score >= 50) return '#d97706'; // Amber
  return '#dc2626'; // Red
}

function getInitials(name: string = '') {
  return name.trim().split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();
}

export function exportSkillReportPdf({ teamSkills, reps, dateFilter, logoUrl }: SkillReportExportOptions) {
  try {
    // Only include reps that have at least one active score (>0) in the selected time filter
    const activeReps = reps.filter(
      (r) => r.overall > 0 || r.discovery > 0 || r.objections > 0 || r.product > 0 || r.closing > 0
    );

    const filterLabel = getFilterLabel(dateFilter);
    const dateStr = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
    const finalLogo = logoUrl || Logo;

    const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>Skill Performance Report - Pitchster</title>
      <style>
        @page {
          size: A4 portrait;
          margin: 14mm 14mm 14mm 14mm;
        }
        * {
          box-sizing: border-box;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        @media print {
          .summary-card, table, tr, td, th, div[style*="border-radius"] {
            -webkit-box-decoration-break: clone !important;
            box-decoration-break: clone !important;
          }
          tr {
            break-inside: avoid !important;
            page-break-inside: avoid !important;
          }
          .header, .footer {
            break-inside: avoid !important;
            page-break-inside: avoid !important;
          }
          .section-title, h1, h2, h3, h4 {
            break-after: avoid !important;
            page-break-after: avoid !important;
          }
        }
        body {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          background: #ffffff;
          color: #1e293b;
          margin: 0;
          padding: 0;
          font-size: 13px;
          line-height: 1.5;
        }
        .header {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          padding-bottom: 18px;
          border-bottom: 2px solid #e2e8f0;
          margin-bottom: 22px;
        }
        .brand-block {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 6px;
        }
        .logo-img {
          height: 42px;
          object-fit: contain;
          margin-bottom: 2px;
        }
        .header-title {
          font-size: 22px;
          font-weight: 800;
          color: #0f172a;
          margin: 0;
          line-height: 1.2;
        }
        .header-subtitle {
          font-size: 13px;
          color: #64748b;
          margin: 0;
        }
        .meta-badges {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 6px;
        }
        .badge {
          background: #f1f5f9;
          border: 1px solid #cbd5e1;
          border-radius: 6px;
          padding: 4px 10px;
          font-size: 11px;
          font-weight: 600;
          color: #334155;
          width: fit-content;
        }
        .badge-primary {
          background: #5a9d5015;
          border-color: #5a9d5044;
          color: #5a9d50;
        }
        
        .section-title {
          font-size: 14px;
          font-weight: 700;
          color: #0f172a;
          margin: 0 0 12px 0;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .summary-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 12px;
          margin-bottom: 24px;
        }
        .summary-card {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          padding: 14px 16px;
        }
        .card-label {
          font-size: 12px;
          color: #64748b;
          font-weight: 600;
          margin-bottom: 6px;
          display: flex;
          justify-content: space-between;
        }
        .card-score {
          font-size: 18px;
          font-weight: 800;
        }
        .progress-track {
          height: 6px;
          background: #e2e8f0;
          border-radius: 3px;
          overflow: hidden;
          margin-top: 8px;
        }
        .progress-fill {
          height: 100%;
          border-radius: 3px;
        }

        table {
          width: 100%;
          border-collapse: collapse;
          border-radius: 10px;
          overflow: hidden;
          border: 1px solid #e2e8f0;
          margin-bottom: 20px;
        }
        th {
          background: #f8fafc;
          color: #475569;
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.8px;
          padding: 11px 14px;
          text-align: left;
          border-bottom: 2px solid #e2e8f0;
        }
        td {
          padding: 12px 14px;
          border-bottom: 1px solid #f1f5f9;
          vertical-align: middle;
          font-size: 13px;
        }
        tr:nth-child(even) {
          background: #fafafa;
        }
        
        .rep-cell {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .avatar-circle {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background: #5a9d50;
          color: #ffffff;
          font-size: 11px;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .score-val {
          font-size: 13px;
          font-weight: 700;
        }
        .trend-badge {
          font-size: 10px;
          font-weight: 700;
          border-radius: 4px;
          padding: 2px 6px;
          display: inline-flex;
          align-items: center;
          gap: 2px;
        }
        .trend-up {
          background: #5a9d5018;
          color: #5a9d50;
        }
        .trend-down {
          background: #dc262618;
          color: #dc2626;
        }
        
        .footer {
          margin-top: 24px;
          padding-top: 12px;
          border-top: 1px solid #e2e8f0;
          display: flex;
          justify-content: space-between;
          color: #94a3b8;
          font-size: 11px;
        }
      </style>
    </head>
    <body>
      <!-- HEADER -->
      <div class="header">
        <div class="brand-block">
          <img src="${finalLogo}" class="logo-img" alt="Pitchster Logo" />
          <h1 class="header-title">Skill Performance Report</h1>
          <p class="header-subtitle">Team Category Skill Index & Rep Benchmarks</p>
        </div>
        <div class="meta-badges">
          <span class="badge badge-primary">Time Filter: ${filterLabel}</span>
          <span class="badge">Generated: ${dateStr}</span>
          <span class="badge">Active Reps: ${activeReps.length}</span>
        </div>
      </div>

      <!-- TEAM SKILL SUMMARY -->
      <h2 class="section-title">Team Skill Averages</h2>
      <div class="summary-grid">
        <div class="summary-card">
          <div class="card-label">
            <span>Discovery</span>
            <span class="card-score" style="color: ${getScoreColor(teamSkills.discovery)}">${teamSkills.discovery}%</span>
          </div>
          <div class="progress-track">
            <div class="progress-fill" style="width: ${teamSkills.discovery}%; background: ${getScoreColor(teamSkills.discovery)}"></div>
          </div>
        </div>
        <div class="summary-card">
          <div class="card-label">
            <span>Objection Handling</span>
            <span class="card-score" style="color: ${getScoreColor(teamSkills.objections)}">${teamSkills.objections}%</span>
          </div>
          <div class="progress-track">
            <div class="progress-fill" style="width: ${teamSkills.objections}%; background: ${getScoreColor(teamSkills.objections)}"></div>
          </div>
        </div>
        <div class="summary-card">
          <div class="card-label">
            <span>Product Knowledge</span>
            <span class="card-score" style="color: ${getScoreColor(teamSkills.product)}">${teamSkills.product}%</span>
          </div>
          <div class="progress-track">
            <div class="progress-fill" style="width: ${teamSkills.product}%; background: ${getScoreColor(teamSkills.product)}"></div>
          </div>
        </div>
        <div class="summary-card">
          <div class="card-label">
            <span>Closing</span>
            <span class="card-score" style="color: ${getScoreColor(teamSkills.closing)}">${teamSkills.closing}%</span>
          </div>
          <div class="progress-track">
            <div class="progress-fill" style="width: ${teamSkills.closing}%; background: ${getScoreColor(teamSkills.closing)}"></div>
          </div>
        </div>
      </div>

      <!-- REPS TABLE -->
      <h2 class="section-title">Rep Skill Breakdown</h2>
      <table>
        <thead>
          <tr>
            <th>Rep Name</th>
            <th>Discovery</th>
            <th>Objection Handling</th>
            <th>Product Knowledge</th>
            <th>Closing</th>
            <th>Overall Avg</th>
            <th>Trend</th>
          </tr>
        </thead>
        <tbody>
          ${activeReps.length > 0 ? activeReps.map((rep) => {
            const discCol = rep.discovery > 0 ? getScoreColor(rep.discovery) : '#94a3b8';
            const objCol = rep.objections > 0 ? getScoreColor(rep.objections) : '#94a3b8';
            const prodCol = rep.product > 0 ? getScoreColor(rep.product) : '#94a3b8';
            const clsCol = rep.closing > 0 ? getScoreColor(rep.closing) : '#94a3b8';
            const overallCol = rep.overall > 0 ? getScoreColor(rep.overall) : '#94a3b8';
            const isUp = rep.trend >= 0;

            return `
              <tr>
                <td>
                  <div class="rep-cell">
                    <div class="avatar-circle">${getInitials(rep.name)}</div>
                    <span style="font-weight: 600; color: #0f172a;">${rep.name}</span>
                  </div>
                </td>
                <td class="score-val" style="color: ${discCol}">${rep.discovery > 0 ? `${Math.round(rep.discovery)}%` : '—'}</td>
                <td class="score-val" style="color: ${objCol}">${rep.objections > 0 ? `${Math.round(rep.objections)}%` : '—'}</td>
                <td class="score-val" style="color: ${prodCol}">${rep.product > 0 ? `${Math.round(rep.product)}%` : '—'}</td>
                <td class="score-val" style="color: ${clsCol}">${rep.closing > 0 ? `${Math.round(rep.closing)}%` : '—'}</td>
                <td class="score-val" style="color: ${overallCol}; font-size: 14px; font-weight: 800;">
                  ${rep.overall > 0 ? `${Math.round(rep.overall)}%` : '—'}
                </td>
                <td>
                  ${rep.overall > 0 && rep.trend !== 0 ? `
                    <span class="trend-badge ${isUp ? 'trend-up' : 'trend-down'}">
                      ${isUp ? '▲ +' : '▼ '}${Math.abs(rep.trend)}%
                    </span>
                  ` : `<span style="color: #94a3b8;">—</span>`}
                </td>
              </tr>
            `;
          }).join('') : `
            <tr>
              <td colspan="7" style="text-align: center; padding: 24px; color: #64748b;">
                No active reps with recorded scores for the selected time filter (${filterLabel}).
              </td>
            </tr>
          `}
        </tbody>
      </table>

      <!-- FOOTER -->
      <div class="footer">
        <span>Generated automatically by Pitchster Performance Analytics</span>
        <span>Confidential & Proprietary</span>
      </div>
    </body>
    </html>
    `;

    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.style.visibility = 'hidden';
    document.body.appendChild(iframe);

    const frameWin = iframe.contentWindow;
    const frameDoc = frameWin?.document || iframe.contentDocument;

    if (frameDoc && frameWin) {
      frameDoc.open();
      frameDoc.write(html);
      frameDoc.close();

      setTimeout(() => {
        try {
          frameWin.focus();
          frameWin.print();
        } catch (e) {
          console.error('Print iframe error:', e);
        } finally {
          setTimeout(() => {
            if (document.body.contains(iframe)) {
              document.body.removeChild(iframe);
            }
          }, 1500);
        }
      }, 400);
    }
  } catch (err) {
    console.error('Export Skill Report PDF Error:', err);
  }
}
